import {
  ErrorMessage,
  IsLoading,
  RegisterFormFieldType
} from '../../../../../types';
import { FetchUsersFilters } from '../../../../users/usersTypes';
import { OperationFields, OperationTeamNanoID } from '../../../operationsTypes';
import {
  DropzoneHelperFileIds,
  DropzoneHelperFiles
} from '../../../../../helpers/dropzone/DropzoneHelper';

import {
  UpdateOperationFormData,
  UpdateOperationFormAccess
} from './UpdateOperationForm.types';

import { CreateOperationStateSelectField } from '../CreateOperationForm/components/CreateOperationStateSelectField';
import { CreateOperationTypeField } from '../CreateOperationForm/components/CreateOperationTypeField';

import { UsersSelectField } from '../../../../users/helpers/UsersSelectField';

import { Form } from '../../../../../helpers/Form';
import { InputField } from '../../../../../helpers/FormFields/InputField';
import { TextAreaField } from '../../../../../helpers/FormFields/TextAreaField';
import { CheckBoxField } from '../../../../../helpers/FormFields/CheckBoxField';
import { ItemsListFieldRequiredProps } from '../../../../../helpers/FormFields/ItemsListField';
import {
  DropzoneField,
  DropzoneFieldRequiredProps
} from '../../../../../helpers/FormFields/DropzoneField';

import {
  attachmentsKeys,
  operationsKeys,
  usersKeys
} from '../../../../../locales/keys';

interface UpdateOperationFormProps {
  form: string;
  teamNanoId?: OperationTeamNanoID;
  watchCreateCompanyRecord?: boolean;
  isLoading?: IsLoading;
  subjectValidationError?: ErrorMessage;
  bodyValidationError?: ErrorMessage;
  dateValidationError?: ErrorMessage;
  completeDatetimeValidationError?: ErrorMessage;
  clientIdValidationError?: ErrorMessage;
  createCompanyRecordError?: ErrorMessage;
  registerBody: RegisterFormFieldType<HTMLTextAreaElement>;
  registerDate: RegisterFormFieldType<HTMLInputElement>;
  registerCompleteDatetime: RegisterFormFieldType<HTMLInputElement>;
  registerSubject: RegisterFormFieldType<HTMLInputElement>;
  registerCreateCompanyRecord: RegisterFormFieldType<HTMLInputElement>;
  access: UpdateOperationFormAccess;
  userSelectInitialFilters?: FetchUsersFilters;
  operationDropZoneFileAttachments: DropzoneHelperFiles | null;
  operationDropZoneFileAttachmentIds: DropzoneHelperFileIds;
}

function UpdateOperationForm({
  form,
  teamNanoId,
  watchCreateCompanyRecord,
  control,
  isLoading,
  subjectValidationError,
  bodyValidationError,
  dateValidationError,
  completeDatetimeValidationError,
  clientIdValidationError,
  createCompanyRecordError,
  registerBody,
  registerDate,
  registerCompleteDatetime,
  registerSubject,
  registerCreateCompanyRecord,
  access,
  userSelectInitialFilters,
  operationDropZoneFileAttachments,
  operationDropZoneFileAttachmentIds
}: UpdateOperationFormProps &
  ItemsListFieldRequiredProps<UpdateOperationFormData> &
  DropzoneFieldRequiredProps<UpdateOperationFormData>) {
  return (
    <Form className="flex-1 overflow-y-auto px-2" id={form}>
      <div className="p-4">
        <div className="space-y-3">
          <div>
            {access.changeClientId ? (
              <UsersSelectField<UpdateOperationFormData>
                control={control}
                i18nPlaceholder={usersKeys.select}
                inputWrapperClassName="relative mt-1"
                name={OperationFields.CLIENT_ID}
                error={clientIdValidationError}
                initialFilters={userSelectInitialFilters}
                disabled={watchCreateCompanyRecord}
                isClearable
              />
            ) : null}

            {access.changeCreateCompanyRecordCheckbox && teamNanoId ? (
              <div className="mt-1.5">
                <CheckBoxField
                  id="create_record_create_company_record_checkbox"
                  ref={registerCreateCompanyRecord.ref}
                  name={registerCreateCompanyRecord.name}
                  i18nLabel={operationsKeys.createCompanyRecord}
                  disabled={isLoading}
                  error={createCompanyRecordError}
                  onChange={registerCreateCompanyRecord.onChange}
                />
              </div>
            ) : null}
          </div>

          {access.changeOperationType ? (
            <div>
              <div className="w-full sm:w-auto sm:flex-1">
                <CreateOperationTypeField
                  disabled={isLoading}
                  i18nLabel={operationsKeys.typeOfActivity}
                  name={OperationFields.OPERATION_TYPE}
                  control={control}
                />
              </div>
            </div>
          ) : null}

          {access.changeSubject ? (
            <div>
              <div className="w-full sm:w-auto sm:flex-1">
                <InputField
                  disabled={isLoading}
                  error={subjectValidationError}
                  i18nLabel={operationsKeys.subject}
                  inputWrapperClassName="relative mt-1"
                  labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  name={registerSubject.name}
                  onChange={registerSubject.onChange}
                  ref={registerSubject.ref}
                  type="text"
                />
              </div>
            </div>
          ) : null}

          {access.changeBody ? (
            <div>
              <div className="w-full sm:w-auto sm:flex-1">
                <TextAreaField
                  disabled={isLoading}
                  i18nLabel={operationsKeys.record}
                  labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  textAreaWrapperClassName="mt-1 relative"
                  name={registerBody.name}
                  onChange={registerBody.onChange}
                  ref={registerBody.ref}
                  rows={5}
                  error={bodyValidationError}
                />
              </div>
            </div>
          ) : null}

          {access.changeState ? (
            <div className="flex gap-3 flex-wrap">
              <div className="w-full sm:w-auto sm:flex-1">
                <CreateOperationStateSelectField<UpdateOperationFormData>
                  disabled={isLoading}
                  i18nLabel={operationsKeys.status}
                  name={OperationFields.STATE}
                  control={control}
                />
              </div>
            </div>
          ) : null}

          {access.changeCompleteDatetime ? (
            <div>
              <div className="w-full sm:w-auto sm:flex-1">
                <InputField
                  disabled={isLoading}
                  error={completeDatetimeValidationError}
                  i18nLabel={operationsKeys.activityDate}
                  inputWrapperClassName="relative mt-1"
                  labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  name={registerCompleteDatetime.name}
                  onChange={registerCompleteDatetime.onChange}
                  ref={registerCompleteDatetime.ref}
                  type="datetime-local"
                />
              </div>
            </div>
          ) : null}

          {access.changeDate ? (
            <div className="flex gap-3 flex-wrap">
              <div className="w-full sm:w-auto sm:flex-1">
                <InputField
                  disabled={isLoading}
                  error={dateValidationError}
                  i18nLabel={operationsKeys.dueDate}
                  inputWrapperClassName="relative mt-1"
                  labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  name={registerDate.name}
                  onChange={registerDate.onChange}
                  ref={registerDate.ref}
                  type="datetime-local"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {access.changeFileAttachmentIds ? (
        <div className="p-4">
          <DropzoneField
            control={control}
            name={OperationFields.FILE_ATTACHMENT_IDS}
            type="fileAttachments"
            labelFor="update-operation-add-files"
            i18nLabel={attachmentsKeys.plural}
            disabled={isLoading}
            initialFiles={operationDropZoneFileAttachments}
            initialFileIds={operationDropZoneFileAttachmentIds}
          />
        </div>
      ) : null}
    </Form>
  );
}

export default UpdateOperationForm;
